import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Wallet, Info, ChevronDown, ChevronUp, Phone, FileText } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';

const Rand = (v) => `R${parseFloat(v || 0).toFixed(2)}`;
const dmy  = (d) => (d ? new Date(d).toLocaleDateString('en-ZA') : '—');

function waLink(phone) {
  if (!phone) return null;
  const digits = String(phone).replace(/\D/g, '');
  const number = digits.startsWith('0') ? '27' + digits.slice(1) : digits;
  return number ? `https://wa.me/${number}` : null;
}

// Live statement PDF — the client always sees their CURRENT balance
const statementUrl = (token) => `https://expressway.xcourier.co.za/api/public/statement/${token}`;

// WhatsApp payment reminder with the statement link included
function waReminderLink(d, overdue) {
  const base = waLink(d.phone);
  if (!base) return null;
  const msg =
    `Hi ${d.name}, a friendly reminder from Expressway Courier: your account has an outstanding balance of ${Rand(d.balance)}` +
    (overdue > 0 ? `, of which ${Rand(overdue)} is overdue` : '') +
    `.\n\nYou can view your full statement here:\n${statementUrl(d.statement_token)}\n\n` +
    `Please contact us on 068 612 9215 to arrange payment. Thank you!`;
  return `${base}?text=${encodeURIComponent(msg)}`;
}

function WhatsAppIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

// Note history + add-note form for one debtor
function NotesPanel({ client }) {
  const qc = useQueryClient();
  const [note, setNote] = useState('');
  const [promised, setPromised] = useState('');
  const [saving, setSaving] = useState(false);

  const { data: notes = [] } = useQuery({
    queryKey: ['debt-notes', client.id],
    queryFn: () => api.get('/billing/debt-notes', { params: { client_id: client.id } }).then((r) => r.data),
  });

  const save = async () => {
    if (!note.trim()) { toast.error('Type a note first'); return; }
    setSaving(true);
    try {
      await api.post('/billing/debt-notes', {
        client_id: client.id, note, promised_date: promised || undefined,
      });
      setNote(''); setPromised('');
      toast.success('Note saved');
      qc.invalidateQueries({ queryKey: ['debt-notes', client.id] });
      qc.invalidateQueries({ queryKey: ['debtors'] });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save note');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-gray-50 border-t p-4 space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-64">
          <label className="block text-xs font-medium text-gray-500 mb-1">
            New note — what did the client say?
          </label>
          <input type="text" value={note} onChange={(e) => setNote(e.target.value)}
            placeholder='e.g. "Spoke to Piet — will pay Friday via EFT"'
            onKeyDown={(e) => e.key === 'Enter' && save()}
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Promised to pay on</label>
          <input type="date" value={promised} onChange={(e) => setPromised(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
        </div>
        <button onClick={save} disabled={saving}
          className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
          {saving ? 'Saving…' : 'Add note'}
        </button>
      </div>

      {notes.length === 0 ? (
        <p className="text-xs text-gray-400">No collection notes for this client yet.</p>
      ) : (
        <div className="bg-white rounded-lg border divide-y">
          {notes.map((n) => {
            const promiseBroken = n.promised_date && new Date(n.promised_date) < new Date().setHours(0, 0, 0, 0);
            return (
              <div key={n.id} className="px-3 py-2 text-sm">
                <p className="text-gray-800">{n.note}</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {new Date(n.created_at).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' })}
                  {n.created_by_name ? ` · ${n.created_by_name}` : ''}
                  {n.promised_date && (
                    <span className={promiseBroken ? 'text-red-600 font-semibold' : 'text-green-600'}>
                      {' '}· promised {dmy(n.promised_date)}{promiseBroken ? ' — DATE PASSED' : ''}
                    </span>
                  )}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function DebtCollection() {
  const [openId, setOpenId] = useState(null);
  const { data: debtors = [], isLoading } = useQuery({
    queryKey: ['debtors'],
    queryFn: () => api.get('/billing/debtors').then((r) => r.data),
    refetchInterval: 60000,
  });

  const totalOwed    = debtors.reduce((s, d) => s + parseFloat(d.balance || 0), 0);
  const totalOverdue = debtors.reduce((s, d) =>
    s + parseFloat(d.overdue_1_30 || 0) + parseFloat(d.overdue_31_60 || 0)
      + parseFloat(d.overdue_61_90 || 0) + parseFloat(d.overdue_90_plus || 0), 0);

  const bucket = (v) => (parseFloat(v || 0) > 0
    ? <span className="font-semibold">{Rand(v)}</span>
    : <span className="text-gray-300">—</span>);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Wallet size={22} className="text-orange-500" />
        <div>
          <h1 className="text-xl font-bold text-gray-800">Debt Collection</h1>
          <p className="text-xs text-gray-400">Who owes money, how overdue it is, and what they promised</p>
        </div>
      </div>

      <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
        <Info size={15} className="text-blue-500 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-blue-900">
          <strong>Every client with unpaid invoices.</strong> The ageing columns show how long money has been
          overdue (past the invoice due date). Click a client to read and add collection notes — record what
          they promise so nothing gets forgotten. Record actual payments on the{' '}
          <Link to="/billing" className="underline font-medium">Billing page</Link>.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl p-4 bg-amber-50 text-amber-700">
          <p className="text-xl font-bold">{Rand(totalOwed)}</p>
          <p className="text-xs mt-0.5 opacity-75">Total outstanding</p>
        </div>
        <div className={`rounded-xl p-4 ${totalOverdue > 0 ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
          <p className="text-xl font-bold">{Rand(totalOverdue)}</p>
          <p className="text-xs mt-0.5 opacity-75">Overdue (past due date)</p>
        </div>
        <div className="rounded-xl p-4 bg-blue-50 text-blue-700">
          <p className="text-xl font-bold">{debtors.length}</p>
          <p className="text-xs mt-0.5 opacity-75">Clients with open invoices</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {isLoading ? <div className="p-8 text-center text-gray-400">Loading…</div>
        : debtors.length === 0 ? (
          <div className="p-10 text-center text-green-600 font-medium">✅ Nobody owes you money — every invoice is paid</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>
                  <th className="px-4 py-3 text-left">Client</th>
                  <th className="px-4 py-3 text-right">Owes</th>
                  <th className="px-4 py-3 text-right">Not due yet</th>
                  <th className="px-4 py-3 text-right">1–30 d</th>
                  <th className="px-4 py-3 text-right">31–60 d</th>
                  <th className="px-4 py-3 text-right">61–90 d</th>
                  <th className="px-4 py-3 text-right">90+ d</th>
                  <th className="px-4 py-3 text-left">Latest note</th>
                  <th className="px-4 py-3 text-center">Contact</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {debtors.map((d) => {
                  const open = openId === d.id;
                  const overdue = parseFloat(d.overdue_1_30 || 0) + parseFloat(d.overdue_31_60 || 0)
                    + parseFloat(d.overdue_61_90 || 0) + parseFloat(d.overdue_90_plus || 0);
                  const promiseBroken = d.promised_date && new Date(d.promised_date) < new Date().setHours(0, 0, 0, 0);
                  return (
                    <React.Fragment key={d.id}>
                      <tr className={`hover:bg-gray-50 ${overdue > 0 ? 'bg-red-50/40' : ''}`}>
                        <td className="px-4 py-3">
                          <button onClick={() => setOpenId(open ? null : d.id)}
                            className="flex items-center gap-1.5 font-medium text-gray-800 hover:text-orange-500">
                            {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            {d.name}
                          </button>
                          <p className="text-xs text-gray-400 ml-5">
                            {d.open_invoices} open invoice{d.open_invoices !== '1' ? 's' : ''}
                            {d.oldest_overdue_days ? ` · oldest ${d.oldest_overdue_days} days overdue` : ''}
                            {d.last_payment ? ` · last paid ${dmy(d.last_payment)}` : ' · never paid'}
                          </p>
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-gray-800">{Rand(d.balance)}</td>
                        <td className="px-4 py-3 text-right text-gray-500">{bucket(d.not_due_yet)}</td>
                        <td className="px-4 py-3 text-right text-amber-600">{bucket(d.overdue_1_30)}</td>
                        <td className="px-4 py-3 text-right text-orange-600">{bucket(d.overdue_31_60)}</td>
                        <td className="px-4 py-3 text-right text-red-600">{bucket(d.overdue_61_90)}</td>
                        <td className="px-4 py-3 text-right text-red-700">{bucket(d.overdue_90_plus)}</td>
                        <td className="px-4 py-3 text-xs text-gray-600 max-w-56">
                          {d.latest_note ? (
                            <>
                              <span className="line-clamp-2">{d.latest_note}</span>
                              {d.promised_date && (
                                <span className={promiseBroken ? 'text-red-600 font-semibold' : 'text-green-600'}>
                                  promised {dmy(d.promised_date)}{promiseBroken ? ' — PASSED' : ''}
                                </span>
                              )}
                            </>
                          ) : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-2.5">
                            {d.phone && (
                              <>
                                <a href={`tel:${d.phone}`} title={`Call ${d.phone}`}
                                  className="text-blue-500 hover:text-blue-600"><Phone size={15} /></a>
                                <a href={waReminderLink(d, overdue)} target="_blank" rel="noreferrer"
                                  title="WhatsApp a payment reminder with their statement link"
                                  className="text-green-600 hover:text-green-700">
                                  <WhatsAppIcon size={15} />
                                </a>
                              </>
                            )}
                            <a href={statementUrl(d.statement_token)} target="_blank" rel="noreferrer"
                              title="Preview their statement PDF (what the client will see)"
                              className="text-orange-500 hover:text-orange-600">
                              <FileText size={15} />
                            </a>
                          </div>
                        </td>
                      </tr>
                      {open && (
                        <tr>
                          <td colSpan={9} className="p-0"><NotesPanel client={d} /></td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
